import { SyncPortError } from '../../shared/errors.js';
import { isValidPin, parseInboxKey } from '../../shared/signaling.js';
import type { InboxCredentials } from './inbox-storage.js';

/** The key matched no inbox on this server. */
export class UnknownInboxKeyError extends SyncPortError {}

export class InboxCreationError extends SyncPortError {
  constructor(readonly reason: 'limit' | 'server') {
    super(`Inbox creation failed: ${reason}`);
  }
}

export type CodeKind = 'transfer' | 'inbox' | 'unknown';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const parseCredentials = (body: unknown): InboxCredentials => {
  if (!isRecord(body) || typeof body['number'] !== 'string' || !isValidPin(body['number']) || typeof body['key'] !== 'string') {
    throw new Error(`Inbox creation response is malformed: ${JSON.stringify(body)}`);
  }
  return { number: body['number'], key: parseInboxKey(body['key']) };
};

export const parseCodeKind = (body: unknown): CodeKind => {
  if (isRecord(body) && (body['kind'] === 'transfer' || body['kind'] === 'inbox' || body['kind'] === 'unknown')) {
    return body['kind'];
  }
  throw new Error(`Code lookup response is malformed: ${JSON.stringify(body)}`);
};

export const createInbox = async (): Promise<InboxCredentials> => {
  const response = await fetch('/api/inboxes', { method: 'POST', cache: 'no-store' });
  if (response.status === 503) {
    throw new InboxCreationError('limit');
  }
  if (!response.ok) {
    throw new InboxCreationError('server');
  }
  return parseCredentials(await response.json());
};

/** Tells whether a 9-digit code belongs to a live transfer or a permanent inbox. */
export const lookupCode = async (digits: string): Promise<CodeKind> => {
  const response = await fetch(`/api/code/${digits}`, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`GET /api/code failed with status ${response.status}`);
  }
  return parseCodeKind(await response.json());
};

/** Asks the server which inbox number belongs to a key. */
export const recoverInboxNumber = async (key: string): Promise<string> => {
  const response = await fetch('/api/inboxes/recover', {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
  if (response.status === 404) {
    throw new UnknownInboxKeyError('No inbox matches this key');
  }
  if (!response.ok) {
    throw new Error(`POST /api/inboxes/recover failed with status ${response.status}`);
  }
  const body: unknown = await response.json();
  if (typeof body !== 'object' || body === null || typeof (body as Record<string, unknown>)['number'] !== 'string') {
    throw new Error(`Recovery response is malformed: ${JSON.stringify(body)}`);
  }
  const number = (body as Record<string, string>)['number'] ?? '';
  if (!isValidPin(number)) {
    throw new Error(`Recovery response carries an invalid number: ${number}`);
  }
  return number;
};

/** Pulls a key (and a number, when present) out of pasted text such as the copied credentials block. */
export const extractPastedCredentials = (text: string): Readonly<{ number: string | null; key: string | null }> => {
  const key = /[A-Za-z0-9_-]{43}/.exec(text)?.[0] ?? null;
  const digits = text.replace(/[A-Za-z0-9_-]{43}/, '').replace(/(\d)[\s-](?=\d)/g, '$1');
  const number = /(?<![0-9])([0-9]{9})(?![0-9])/.exec(digits)?.[1] ?? null;
  return { number, key };
};
